import { MonthsPairs, UploadedFile } from "@dtm-frontend/shared/utils";
import { Address } from "./address.models";
import { PhoneNumber } from "./phone-number.models";
import { MissionType } from "./tactical.models";
import { UavType } from "./uav.models";

export enum ProtectedEnvironmentMitigationType {
    ABSENCE = "ABSENCE",
    LOW_ROBUSTNESS = "LOW_ROBUSTNESS",
    AVERAGE_ROBUSTNESS = "AVERAGE_ROBUSTNESS",
}

export enum OperationalRestrictionsMitigationType {
    ABSENCE = "ABSENCE",
    AVERAGE_ROBUSTNESS = "AVERAGE_ROBUSTNESS",
    HIGH_ROBUSTNESS = "HIGH_ROBUSTNESS",
}

export enum GroundObservationMitigationType {
    ABSENCE = "ABSENCE",
    LOW_ROBUSTNESS = "LOW_ROBUSTNESS",
}

export interface DtmLocation {
    id?: string;
    name: string;
}

export enum OperationAreaType {
    DifferentRoutes = "DIFFERENT_ROUTES",
    FixedRoute = "FIXED_ROUTE",
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface AvailabilityDistribution extends Array<[MonthsPairs, Record<string, string[]>]> {}
export interface OperationRestrictionsModel {
    permitNumber: string;
    permitConfirmationNumber?: string;
    expirationDate: {
        dateFrom: Date;
        dateTo: Date;
    };
    operationTarget: string;
    localization?: LocalizationType;
    dtm?: DtmLocation;
    locationDescription: string;
    riskMethodology: RiskAssessmentReference;
    sail: Sail;
    operationType: MissionType;
    isDangerousGoodsTransport: boolean;
    kmlFile?: UploadedFile[];
    pdraNumber: string | null;
    operationArea?: OperationAreaType;
    location?: {
        type: LocalizationType;
        dtmDesignator: string;
    };
}

export interface BasicDataModel {
    caaContactUser: CaaContactPerson;
    operator: Operator;
    operatorContact: ContactPerson;
    operationRestrictions: OperationRestrictionsModel;
    groundRiskCharacteristic: GroundRiskCharacteristicModel;
    operationalVolumeHeight: OperationalVolumeHeightModel;
    finalRiskArea: FinalRiskAreaModel;
    groundRiskMitigation: GroundRiskMitigationModel;
    airRiskMitigation: AirRiskMitigationModel;
    additionalAccidentType: AdditionalAccidentModel;
}

export interface UavDetailsModel {
    uavModel: PermitUavModel;
    operatedNumber: number;
    isDroneSwarm: boolean;
    serialNumbers: string;
    maxUavWidth: number;
    maxFlightSpeed: number;
    takeOffWeight: number;
    additionalTechRequirements: string;
    designVerificationReport: string;
    certificateOfAirworthiness: string;
    noiseCertificate: string;
    riskMitigation: GroundRiskMitigationType;
    containmentReachedLevel: ContainmentLevelReachedType;
    comments?: string;
}

export interface Permit {
    id?: string;
    basicData: BasicDataModel;
    uavDetails: UavDetailsModel;
}

export interface AdditionalAccidentModel {
    hasInsurance: boolean;
    operationsManualReference: string;
    additionalLimitations: string;
}

export interface FinalRiskAreaModel {
    operationArea: OperationalArc;
    adjacentArea: OperationalArc;
    operationalAirSpaces: OperationalAirspaceClassType[];
    otherAirspace?: string;
}

export interface OperationalVolumeHeightModel {
    operationalVolumeHeightLimit: number;
}

export interface GroundRiskMitigationModel {
    strategicMitigationMeasure?: GroundRiskMitigationType;
    emergencyResponsePlan?: GroundRiskMitigationType;
    avoidingFlightsOverPeople?: AvoidingFlightsOverPeopleType;
    protectedEnvironment?: ProtectedEnvironmentMitigationType;
    operationalRestrictions?: OperationalRestrictionsMitigationType;
    groundObservation?: GroundObservationMitigationType;
    dataSource?: DataSourceType | null;
    selectedHours?: AvailabilityDistribution;
    containmentRequirementsLevel?: ContainmentLevelReachedType;
}

export interface AirRiskMitigationModel {
    strategicMitigationMeasure: AirRiskMitigationType;
    tacticalAirRiskMitigationMeasure: TacticalAirRiskMitigationType;
    tacticalAirRiskMitigationRequirements?: string;
    competencies: Competency[];
    additionalCompetencies: string[];
    competenciesDescription: string;
    staffCompetencies: string[];
    staffCompetenciesDescription: string;
    operationTimeRanges?: { start: Date; end: Date }[];
}

export interface GroundRiskCharacteristicModel {
    groundAreaCharacteristic: GroundAreaCharacteristicType;
    controlledGroundAreaCharacteristic?: ControlledGroundAreaCharacteristicType;
    finalGroundRiskClass?: number;
    adjacentGroundAreaCharacteristic: AdjacentGroundAreaCharacteristicType;
    adjacentGroundAreaExtent?: number;
}

export interface Competency {
    id: string;
    name: string;
}

export enum GroundRiskMitigationType {
    Absence = "ABSENCE",
    LowRobustness = "LOW_ROBUSTNESS",
    AverageRobustness = "AVERAGE_ROBUSTNESS",
    HighRobustness = "HIGH_ROBUSTNESS",
}

export enum AvoidingFlightsOverPeopleType {
    Absence = "ABSENCE",
    LowRobustness = "LOW_ROBUSTNESS",
}

export enum DataSourceType {
    StatisticalHistorical = "STATISTICAL_HISTORICAL",
    OwnData = "OWN_DATA",
}

export enum AirRiskMitigationType {
    Absence = "ABSENCE",
    OperationalMitigation = "OPERATIONAL_MITIGATION",
    CommonRulesAndAirspace = "COMMON_RULES_AND_AIRSPACE",
    OperationalMitigationAndCommonRulesAndAirspace = "OPERATIONAL_MITIGATION_COMMON_RULES_AND_AIRSPACE",
}

export enum GroundAreaCharacteristicType {
    Controlled = "CONTROLLED",
    PeopleAssembly = "PEOPLE_ASSEMBLY",
    DenseUrban = "DENSE_URBAN",
    Urban = "URBAN",
    Suburban = "SUBURBAN",
    Populated = "POPULATED",
    SparselyPopulated = "SPARSELY_POPULATED",
    Rural = "RURAL",
    LightlyPopulated = "LIGHTLY_POPULATED",
    Remote = "REMOTE",
}

export enum ControlledGroundAreaCharacteristicType {
    Remote = GroundAreaCharacteristicType.Remote,
    LightlyPopulated = GroundAreaCharacteristicType.LightlyPopulated,
    Rural = GroundAreaCharacteristicType.Rural,
    SparselyPopulated = GroundAreaCharacteristicType.SparselyPopulated,
    Populated = GroundAreaCharacteristicType.Populated,
    Suburban = GroundAreaCharacteristicType.Suburban,
    Urban = GroundAreaCharacteristicType.Urban,
    DenseUrban = GroundAreaCharacteristicType.DenseUrban,
}

export enum AdjacentGroundAreaCharacteristicType {
    PeopleAssembly = GroundAreaCharacteristicType.PeopleAssembly,
    DenseUrban = GroundAreaCharacteristicType.DenseUrban,
    Urban = GroundAreaCharacteristicType.Urban,
    Suburban = GroundAreaCharacteristicType.Suburban,
    Populated = GroundAreaCharacteristicType.Populated,
    SparselyPopulated = GroundAreaCharacteristicType.SparselyPopulated,
    Rural = GroundAreaCharacteristicType.Rural,
    LightlyPopulated = GroundAreaCharacteristicType.LightlyPopulated,
    Remote = GroundAreaCharacteristicType.Remote,
    Controlled = GroundAreaCharacteristicType.Controlled,
}

export enum ContainmentLevelReachedType {
    BASIC = "BASIC",
    HIGHER = "HIGHER",
    LOW = "LOW",
    MEDIUM = "MEDIUM",
    HIGH = "HIGH",
}

export enum TacticalAirRiskMitigationType {
    VlosSeeAndAvoid = "VLOS_SEE_AND_AVOID",
    BVlosArcA = "BVLOS_ARC_A",
    BVlosArcB = "BVLOS_ARC_B",
    BVlosArcC = "BVLOS_ARC_C",
    BVlosArcD = "BVLOS_ARC_D",
}

export enum OperationalAirspaceClassType {
    C = "C",
    D = "D",
    G = "G",
    USpace = "U_SPACE",
    Other = "OTHER",
}

export enum Sail {
    Sail1 = 1,
    Sail2 = 2,
    // eslint-disable-next-line no-magic-numbers
    Sail3 = 3,
    // eslint-disable-next-line no-magic-numbers
    Sail4 = 4,
    // eslint-disable-next-line no-magic-numbers
    Sail5 = 5,
    // eslint-disable-next-line no-magic-numbers
    Sail6 = 6,
}

export enum OperationalArc {
    ARC_A = 1,
    ARC_B = 2,
    // eslint-disable-next-line no-magic-numbers
    ARC_C = 3,
    // eslint-disable-next-line no-magic-numbers
    ARC_D = 4,
}

export enum LocalizationType {
    Kml = "KML",
    Dtm = "DTM",
    Mission_Plan = "MISSION_PLAN",
}

export enum RiskAssessmentReference {
    SORA2_0 = "SORA2_0",
    SORA2_0_ALTMOC = "SORA2_0_ALTMOC",
    PDRA = "PDRA",
}

export enum SpecificPermitFlightWizardSteps {
    BasicInfo = "BasicInfo",
    UavDetails = "UavDetails",
    Summary = "Summary",
}

export interface Operator {
    id: string;
    name: string;
    number: string;
    models?: PermitUavModel[];
}

export interface ContactPerson {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: PhoneNumber;
    position?: string;
}

export interface AssociationOperator extends Operator {
    associationNationalCourtRegister?: string;
    associationRegistrationNumber?: string;
    address: Address;
}

export interface PermitUavModel {
    id: string;
    name: string;
    manufacturer: string;
    type: UavType;
    technicalSpecification: {
        maxDroneWidth: number;
        takeOffMass: number;
        maxFlightSpeed: number;
    };
}

export interface CaaContactPerson {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: PhoneNumber;
}

export enum AssociationOperationType {
    VLOS = "VLOS",
    FPV = "FPV",
}

export interface AssociationPermit {
    caaContact: CaaContactPerson;
    flightPurpose: string;
    id?: string;
    isInsurance: boolean;
    locations: string;
    operationType: AssociationOperationType;
    operatorContact: ContactPerson;
    permitNumber: string;
    type?: PermitType;
    expirationDate: {
        dateFrom: Date;
        dateTo: Date;
    };
    associationInfo: AssociationOperator;
}

export enum PermitType {
    Association = "ASSOCIATION",
    OperationPermission = "OPERATION_PERMISSION",
    CrossBorder = "CROSS_BORDER",
}
